import { DOCUMENT, inject, Service, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export type Lang = 'uz' | 'ru';

export const SUPPORTED_LANGS: readonly Lang[] = ['uz', 'ru'];

/** Also the ngx-translate fallback language — every key is guaranteed to resolve through it. */
export const DEFAULT_LANG: Lang = 'uz';

const LANG_STORAGE_KEY = 'qr-menu:lang';
const TITLE_KEY = 'app.title';

function isLang(value: string | null): value is Lang {
  return (SUPPORTED_LANGS as readonly string[]).includes(value ?? '');
}

function readStoredLang(): Lang {
  try {
    const value = localStorage.getItem(LANG_STORAGE_KEY);
    return isLang(value) ? value : DEFAULT_LANG;
  } catch {
    // localStorage unavailable (private mode / blocked storage) — fall back to the default.
    return DEFAULT_LANG;
  }
}

/**
 * Owns the active language: the signal every localized computed reads, the persisted choice, and
 * the `<html lang>` / `document.title` side effects.
 *
 * Zoneless contract: the dictionary is ALWAYS loaded before `lang()` changes (both in `init()` and
 * in `setLang()`), so any computed that re-runs because `lang()` changed can safely resolve its
 * strings with `TranslateService.instant()` — there is no intermediate render against a missing
 * dictionary.
 */
@Service()
export class LanguageService {
  private translate = inject(TranslateService);
  private doc = inject(DOCUMENT);

  private readonly _lang = signal<Lang>(readStoredLang());
  readonly lang = this._lang.asReadonly();

  /** Awaited by the app initializer: the first render must never paint raw keys. The loader is
   * static (bundled dictionaries), so this resolves in a microtask and can't fail. */
  async init(): Promise<void> {
    await firstValueFrom(this.translate.use(this._lang()));
    this.applyDocument();
  }

  async setLang(next: Lang): Promise<void> {
    if (next === this._lang()) return;
    await firstValueFrom(this.translate.use(next));
    // Only now: the dictionary is in, so every computed reading lang() sees the new strings.
    this._lang.set(next);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // Storage unavailable — the choice still holds for this session.
    }
    this.applyDocument();
  }

  /**
   * The one reactive-translation helper: touches `lang()` so any computed (or template read)
   * calling it re-runs on a language switch, then resolves through `instant()` — safe by the
   * class contract above. Use this instead of hand-rolling lang()+instant() at call sites.
   */
  t(key: string, params?: Record<string, unknown>): string {
    this._lang();
    return String(this.translate.instant(key, params));
  }

  private applyDocument(): void {
    this.doc.documentElement.lang = this._lang();
    this.doc.title = String(this.translate.instant(TITLE_KEY));
  }
}
